<template>
  <div>
    <v-dialog v-model="editDialog" max-width="600px" persistent>
      <v-card>
        <v-toolbar card color="primary" dark>
          <v-toolbar-title>
            {{ formTitle }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-container grid-list-md>
            <connection-manager
              ref="editConnectionManager"
              v-model="connecting"
            >
            </connection-manager>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-layout row wrap>
                <v-flex
                  xs12
                  v-for="(header, index) in editableHeaders"
                  :key="index"
                >
                  <v-select
                    v-if="header.type === 'select'"
                    :items="toOptions(header.options)"
                    :placeholder="toPlaceholder(header.text)"
                    :label="header.text"
                    :hint="
                      itemToEditIndex > -1 ? header.editHint : header.createHint
                    "
                    persistent-hint
                    :required="isRequired(header)"
                    :rules="isRequired(header) ? [rules.required] : []"
                    v-model="itemToEdit[header.value]"
                    :disabled="connecting"
                  >
                  </v-select>
                  <crud-remote-select
                    v-else-if="header.type === 'select_remote'"
                    :header="header"
                    :editing="itemToEditIndex > -1"
                    :disabled="connecting"
                    :required="isRequired(header)"
                    v-model="itemToEdit[header.value]"
                  >
                  </crud-remote-select>
                  <!--location model is not dynamic like the other fields. to be checked-->
                  <google-place-input
                    v-else-if="header.type === 'location'"
                    :header="header"
                    :disabled="connecting"
                    id="destination"
                    country="KE"
                    :clearable="false"
                    :enable-geolocation="true"
                    label="Enter location"
                    placeholder="Location"
                    prepend-icon="edit_location"
                    :required="true"
                    :rules="isRequired(header) ? [rules.required] : []"
                    :load-google-api="false"
                    :google-api-key="$app.googleMapsKey"
                    ref="locationInput"
                    :hint="
                      !placeResultData ? '' : placeResultData.formatted_address
                    "
                    persistent-hint
                    :hide-details="false"
                    types="establishment"
                    v-on:placechanged="onLocationEntered"
                    v-model="itemToEdit[header.value]"
                  >
                  </google-place-input>
                  <v-text-field
                    v-else
                    :placeholder="toPlaceholder(header.text)"
                    :label="header.text"
                    :type="header.type"
                    :required="isRequired(header)"
                    :rules="isRequired(header) ? [rules.required] : []"
                    :hint="
                      itemToEditIndex > -1 ? header.editHint : header.createHint
                    "
                    persistent-hint
                    :disabled="connecting"
                    v-model="itemToEdit[header.value]"
                  >
                  </v-text-field>
                </v-flex>
              </v-layout>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            flat
            :disabled="connecting"
            @click.native="close"
            >Cancel
          </v-btn>
          <v-btn
            color="primary darken-1"
            flat
            :disabled="connecting || !valid"
            @click.native="save"
            >Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="600px">
      <v-card v-if="itemToView">
        <v-card-text>
          <v-data-table
            hide-headers
            hide-actions
            :headers="viewItemHeaders"
            :items="viewableHeaders"
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item.text }}</td>
              <td>{{ manager.toValue(props.item, itemToView) }}</td>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red darken-1" flat @click.native="itemToView = null"
            >Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" persistent>
      <v-card v-if="itemToDelete">
        <v-card-text>
          <connection-manager ref="deleteConnectionManager" v-model="deleting">
          </connection-manager>

          <p>Confirm you want to delete</p>
          <b>{{ manager.nameOnDelete(itemToDelete) }}</b>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="red" flat :disabled="deleting" @click.native="doDelete"
            >Delete
          </v-btn>
          <v-btn
            color="primary"
            flat
            :disabled="deleting"
            @click.native="itemToDelete = null"
            >Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <connection-manager ref="connectionManager" v-model="connecting">
    </connection-manager>

    <v-card>
      <v-toolbar color="transparent" dense flat card>
        <v-btn
          color="primary"
          class="mx-5"
          v-if="creatable"
          @click.native="editDialog = true"
        >
          Add
        </v-btn>
        <div
          v-for="(action, index) in extraTopActions"
          :key="index"
          name="extraTopActions"
        >
          <v-btn
            :color="action.color ? action.color : 'primary'"
            class="mx-5"
            v-if="manager.showTopAction(action, items, currentFilter)"
            @click.native="manager.onTopAction(action, items, currentFilter)"
          >
            <template>
              {{ action.name }}
            </template>
          </v-btn>
        </div>
        <v-text-field
          append-icon="search"
          label="Search"
          single-line
          hide-details
          v-model="search"
        >
        </v-text-field>
        <v-menu transition="slide-y-transition" bottom offset-y>
          <v-btn
            slot="activator"
            color="primary"
            dark
            flat
            v-if="filters && filters.length"
          >
            {{ currentFilter ? currentFilter.name : 'Filters' }}
            <v-icon right>keyboard_arrow_down</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile
              v-for="(filter, i) in filters"
              :key="i"
              @click="currentFilter = filter"
            >
              <v-list-tile-title>{{ filter.name }}</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-toolbar>
      <v-data-table
        :headers="browsableHeaders"
        :items="items"
        item-key="id"
        :search="search"
        expand
        :no-data-text="connecting ? '' : 'No data available'"
        :rows-per-page-items="rowsPerPageItems"
        :pagination.sync="pagination"
        class="elevation-0"
      >
        <template slot="items" slot-scope="props">
          <tr
            @click="
              extraInlineActions.length
                ? (props.expanded = !props.expanded)
                : ''
            "
          >
            <td
              :class="['text-xs-' + header.align]"
              v-for="(header, index) in browsableHeaders"
              :key="index"
            >
              <template v-if="header.value !== 'actions'">
                {{ manager.toValue(header, props.item) }}
              </template>
            </td>
            <td>
              <v-btn-toggle light>
                <v-btn flat @click="viewItem(props.item)">
                  <v-icon color="primary">visibility</v-icon>
                </v-btn>
                <v-btn
                  flat
                  v-if="manager.deletable(props.item)"
                  @click="deleteItem(props.item)"
                >
                  <v-icon color="red">delete</v-icon>
                </v-btn>

                <v-btn
                  flat
                  v-if="manager.editable(props.item)"
                  @click="editItem(props.item)"
                >
                  <v-icon color="success">edit</v-icon>
                </v-btn>
                <v-menu offset-y bottom>
                  <v-btn
                    icon
                    v-if="extraOverflowActions.length"
                    slot="activator"
                  >
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                  <v-list>
                    <v-list-tile
                      v-for="(action, index) in extraOverflowActionsToShow"
                      :key="index"
                      @click="manager.fireAction(action, props.item)"
                    >
                      <v-list-tile-title>{{ action.name }}</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-btn-toggle>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <v-card flat>
            <v-card-actions>
              <v-spacer />
              <v-btn
                v-for="(action, index) in showInlineActionItems(props.item)"
                :color="action.color"
                class="ml-0 mr-1"
                small
                @click.native="
                  manager.onInlineActionClicked(
                    action,
                    props.item,
                    currentFilter
                  )
                "
                :key="index"
              >
                {{ action.name }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-data-table>
      <Pagination
        v-if="paginate"
        :totalPages.sync="paginationState.totalPages"
        :currentPage.sync="paginationState.currentPage"
        @pagechanged="pagechanged"
      ></Pagination>
    </v-card>
  </div>
</template>
<script>
import ConnectionManager from './ConnectionManager'
import CrudRemoteSelect from './CrudRemoteSelect'
import GooglePlaceInput from './GooglePlaceInput'
import Pagination from './Pagination.vue'

export default {
  name: 'Crud',
  components: {
    CrudRemoteSelect,
    ConnectionManager,
    GooglePlaceInput,
    Pagination
  },
  props: {
    // Specify the endpoint to connect to
    resource: {
      type: String,
      required: true
    },
    paginate: {
      type: Boolean,
      required: false,
      default: false
    },
    paginationResource: {
      type: String,
      required: false,
      default: ''
    },
    // Actions to be shown at the top, next to the search bar
    extraTopActions: {
      type: Array,
      required: false,
      default: function() {
        return []
      }
    },

    // Actions to be shown inline, below the row when the item is clicked
    extraInlineActions: {
      type: Array,
      required: false,
      default: function() {
        return []
      }
    },

    // Actions to be shown when the overflow icon is clicked
    extraOverflowActions: {
      type: Array,
      required: false,
      default: function() {
        return []
      }
    },

    // An array of filters to show, filters are show at the right of the search
    filters: {
      type: Array,
      required: false
    },

    // If should show a custom view dialog
    customViewDialog: {
      type: Boolean,
      required: false
    },

    // If the add button should be shown
    creatable: {
      type: Boolean,
      required: false,
      default: true
    },

    // A manager
    manager: {
      type: Object,
      required: true
    },

    // Headers that should not be shown
    hiddenHeaders: {
      type: Array,
      required: false,
      default: function() {
        return []
      }
    }
  },
  // data
  data: () => ({
    editDialog: false,
    viewDialog: false,
    deleteDialog: false,
    valid: false,
    connecting: false,
    deleting: false,
    headers: [],
    browsableHeaders: [],
    viewableHeaders: [],
    viewItemHeaders: [
      {
        text: 'Name',
        value: 'name',
        align: 'left'
      },
      {
        text: 'Value',
        value: 'value',
        align: 'left'
      }
    ],
    rowsPerPageItems: [10],
    pagination: {
      rowsPerPage: 10
    },
    items: [],
    rules: {
      required: value => !!value || 'This field is required.'
    },
    itemToEditIndex: -1,
    itemToEdit: {},
    itemToDeleteIndex: -1,
    itemToDelete: null,
    itemToView: null,
    currentFilter: null,
    search: null,
    placeResultData: null,
    location: null,
    addressData: null,
    paginationState: {
      perPage: 10,
      currentPage: 1,
      totalPages: 5,
      totalCount: 0
    }
  }),
  computed: {
    extraOverflowActionsToShow() {
      return this.extraOverflowActions.filter(action =>
        this.manager.showExtraOverflowActions(action, this.currentFilter)
      )
    },
    formTitle() {
      return this.itemToEditIndex === -1 ? 'New Item' : 'Edit Item'
    },
    editableHeaders() {
      let that = this
      return this.headers.filter(header => {
        return that.isEditable(header)
      })
    }
  },
  watch: {
    editDialog(val) {
      val || this.close()
    },
    itemToView(val) {
      this.viewDialog = !!val
    },
    itemToDelete(val) {
      this.deleteDialog = !!val
    },
    currentFilter(val) {
      if (val) {
        this.loadItems(1)
      }
    }
  },
  methods: {
    showInlineActionItems(item) {
      let that = this
      return this.extraInlineActions.filter(inlineAction => {
        return that.manager.showInlineAction(
          inlineAction,
          item,
          that.currentFilter
        )
      })
    },
    pagechanged(nextPage) {
      this.loadItems(nextPage)
    },
    isRequired(header) {
      if (this.itemToEditIndex > -1) {
        return header.editRequired
      } else {
        return header.createRequired
      }
    },
    onLocationEntered(addressData, placeResultData) {
      this.addressData = addressData
      this.placeResultData = placeResultData
      this.location = placeResultData.name
    },
    isEditable(header) {
      if (this.itemToEditIndex > -1) {
        return header.editable
      } else {
        return header.creatable
      }
    },

    // Remove all the items
    clearItems() {
      this.items = []
    },

    // Set the items
    setItems(items) {
      this.items = items
    },

    // Generate placeholder based on the value of a header
    toPlaceholder(value) {
      return 'Enter ' + value.toLowerCase()
    },

    // Generate an array where options for select header are comma separated
    toOptions(options) {
      return options.split(',')
    },

    isHiddenHeader() {
      return this.hiddenHeaders.find()
    },

    loadItems(nextPage) {
      this.items = []
      let that = this
      this.$refs.connectionManager.get(
        this.paginate ? this.paginationResource : this.resource,
        {
          onSuccess(response) {
            that.$app.log(response.data)
            that.$app.log(response.data.meta)
            that.items = []
            that.items = that.items.concat(response.data.data)
            that.headers = []
            that.headers = that.headers.concat(response.data.meta.headers)
            that.browsableHeaders = that.headers.filter(header => {
              // Find if the header is in the hidden headers
              return (
                header.browsable &&
                !that.hiddenHeaders.find(h => h.text === header.text) &&
                !that.manager.hideHeader(header, that.currentFilter)
              )
            })
            that.viewableHeaders = that.headers.filter(header => {
              return (
                header.viewable &&
                !that.hiddenHeaders.find(h => h.text === header.text) &&
                !that.manager.hideHeader(header, that.currentFilter)
              )
            })
            that.browsableHeaders.push({
              text: 'Actions',
              value: 'actions',
              align: 'center',
              sortable: false
            })
            if (response.data.paginator) {
              let paginator = response.data.paginator
              that.paginationState.totalPages = paginator.totalPages
              that.paginationState.totalCount = paginator.totalCount
              that.paginationState.currentPage = paginator.currentPage
            }
          }
        },
        {
          filter: this.currentFilter ? this.currentFilter.value : '',
          page: nextPage,
          size: this.paginationState.perPage
        }
      )
    },
    viewItem(item) {
      if (!this.manager.hasCustomView(item, this.viewableHeaders)) {
        this.$app.log(item)
        this.itemToView = item
      }
    },
    editItem(item) {
      this.itemToEditIndex = this.items.indexOf(item)
      this.itemToEdit = Object.assign({}, item)
      this.editDialog = true
    },

    deleteItem(item) {
      this.itemToDeleteIndex = this.items.indexOf(item)
      this.itemToDelete = item
      this.$app.log(item)
      // confirm('Are you sure you want to delete this item?') && this.items.splice(deleteIndex, 1)
    },
    doDelete() {
      let that = this
      this.$refs.deleteConnectionManager.delete(
        this.resource + '/' + this.itemToDelete.id,
        {
          onSuccess(response) {
            that.$app.log(response)
            that.items.splice(that.itemToDeleteIndex, 1)
            that.itemToDelete = null
            that.itemToDeleteIndex = -1
            that.deleting = false
            that.$refs.deleteConnectionManager.reset()
          }
        }
      )
    },

    // Updates the given item
    updateItem(updatedItem) {
      this.$app.log('updateItem')
      let itemToUpdate = this.items.find(item => item.id === updatedItem.id)
      let indexOfItemToUpdate = this.items.indexOf(itemToUpdate)
      Object.assign(this.items[indexOfItemToUpdate], updatedItem)
    },
    removeItem(updatedItem) {
      this.$app.log('removeItem')
      this.items = this.items.filter(item => item.id !== updatedItem.id)
    },
    close() {
      this.editDialog = false
      setTimeout(() => {
        this.itemToEdit = Object.assign({}, {})
        this.itemToEditIndex = -1
        this.$refs.form.reset()
        this.$refs.editConnectionManager.reset()
      }, 300)
    },
    save() {
      // location has been added manually.. to be checked later
      this.itemToEdit['locationName'] = this.location
      this.itemToEdit['formattedAddress'] = this.placeResultData
        ? this.placeResultData.formatted_address
        : ''
      this.itemToEdit['vicinity'] = this.placeResultData
        ? this.placeResultData.vicinity
        : ''
      this.itemToEdit['latitude'] = this.placeResultData
        ? this.placeResultData.geometry.location.lat()
        : ''
      this.itemToEdit['longitude'] = this.placeResultData
        ? this.placeResultData.geometry.location.lng()
        : ''
      this.$app.log(this.itemToEdit)
      let that = this
      if (this.itemToEditIndex > -1 && this.$refs.form.validate()) {
        this.$refs.editConnectionManager.patch(
          this.resource + '/' + this.itemToEdit.id,
          {
            onSuccess(response) {
              Object.assign(
                that.items[that.itemToEditIndex],
                response.data.data
              )
              that.close()
            }
          },
          this.itemToEdit
        )
      } else if (this.$refs.form.validate()) {
        this.$refs.editConnectionManager.post(
          this.resource,
          {
            onSuccess(response) {
              that.items.push(response.data.data)
              that.close()
            }
          },
          this.itemToEdit
        )
      }
    }
  },
  mounted() {
    if (this.filters) {
      this.currentFilter = this.filters[0]
    } else {
      this.loadItems(this.paginationState.currentPage)
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/Crud.vue